import { Editor, NodeViewWrapper } from '@tiptap/react';
import React, { useContext, useMemo } from 'react';
import DocumentContext from '../../../contexts/DocumentContext.context';
import isDirectNodeSelection from '../../utils/isDirectNodeSelection';
import { isRegistrationSettled } from '../../../helpers/isRegistrationSettled.helper';
import { TPlacedBlock } from '../../../types';
import { usePlaceBlock } from '../../../api';
import { TERMINOLOGY } from '@shared/i18n';
import { useNodeRegistration } from '../../../hooks/useNodeRegistration.hook';
import classNames from 'classnames';

type TAttrs = TPlacedBlock & { loadedId?: string };

type TProps = {
  node: {
    attrs: any; // must be any for compatibility with Tiptap, TAttrs is used in the component
  };
  editor: Editor;
  updateAttributes: (attributes: any) => void;
  getPos: () => number | undefined;
  /** TipTap's own remove: it resolves the position and deletes the node's range. */
  deleteNode: () => void;
  selected: boolean;
};

const Component = React.memo(({ node, editor, updateAttributes, getPos, deleteNode, selected }: TProps) => {
  const attrs: TAttrs = node.attrs;
  const { doc, resolvedItemNames, version, isEdit, versionId } = useContext(DocumentContext);
  const placeBlock = usePlaceBlock();

  useNodeRegistration({
    id: attrs.id,
    loadedId: attrs.loadedId,
    isEdit,
    registration: !versionId ? 'unknown' : doc.blocksData[attrs.id]?.versions[versionId] ? 'registered' : 'missing',
    register: async () => {
      const block: TPlacedBlock = {
        id: attrs.id,
        item: attrs.item,
        itemType: attrs.itemType,
        versionMode: attrs.versionMode,
        ...(attrs.versionId != null ? { versionId: attrs.versionId } : {}),
      };
      await placeBlock.mutateAsync({ docId: doc._id, block });
    },
    editor,
    getPos,
    removeNode: deleteNode,
  });

  const [isError, value] = useMemo(() => {
    if (!isRegistrationSettled(attrs)) return [false, <span>Loading {TERMINOLOGY.inlineBlock.lower}...</span>];

    const blockData = doc.blocksData[attrs.id];
    const blockVersionEntry = versionId ? blockData?.versions[versionId] : undefined;
    if (!blockVersionEntry) return [true, `Loading ${TERMINOLOGY.inlineBlock.lower} failed.`];

    const value = blockVersionEntry?.variantFolderId ? blockVersionEntry.value : ''; // space to avoid empty span

    return [!blockData.itemActive, value || ' '];
  }, [attrs.id, attrs.loadedId, doc.blocksData, versionId]);

  return (
    <NodeViewWrapper
      as="span"
      id={attrs.id}
      data-drag-handle
      className={classNames(`inline-block-styled ${isError ? 'inline-block-error' : ''}`, {
        // darker error color, because of green background
        selected: isDirectNodeSelection(editor, selected, isEdit),
      })}
    >
      <span>{value}</span>
    </NodeViewWrapper>
  );
});

export default Component;
